import { useForm } from "react-hook-form";
import { formatLapTime } from "@/utils/timeFormat";
import type { UpdateLapRequest, Lap } from "@/types";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface LapFormProps {
  onSubmit: (data: UpdateLapRequest) => void;
  onCancel: () => void;
  initialData: Lap;
  isLoading?: boolean;
  serverErrors?: Record<string, string>;
}

type FormData = {
  notes?: string;
  isValid: boolean;
};

const readOnlyInputClass =
  "input font-mono bg-secondary-50 text-secondary-500";

export default function LapForm({
  onSubmit,
  onCancel,
  initialData,
  isLoading,
  serverErrors = {},
}: LapFormProps) {
  const { t } = useTranslation(["pages", "common"]);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<FormData>({
    defaultValues: {
      notes: initialData.notes ?? "",
      isValid: initialData.isValid,
    },
  });

  // Apply server side validation errors
  useEffect(() => {
    if (Object.keys(serverErrors).length > 0) {
      // clear previous server errors first
      clearErrors();
      Object.entries(serverErrors).forEach(([field, message]) => {
        setError(field as keyof FormData, { type: "server", message });
      });
    }
  }, [serverErrors, setError, clearErrors]);

  const handleFormSubmit: import("react-hook-form").SubmitHandler<FormData> = (
    data,
  ) => {
    const submitData: UpdateLapRequest = {
      notes: data.notes || undefined,
      isValid: data.isValid,
    };

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="lapNumber"
            className="block text-sm font-medium text-secondary-700 mb-2"
          >
            {t("pages:sessions.detail.laps.form.lapNumber")}
          </label>
          <input
            type="number"
            id="lapNumber"
            value={initialData.lapNumber}
            readOnly
            tabIndex={-1}
            className={readOnlyInputClass}
          />
        </div>

        <div>
          <label
            htmlFor="lapTimeString"
            className="block text-sm font-medium text-secondary-700 mb-2"
          >
            {t("pages:sessions.detail.laps.form.lapTime")}
          </label>
          <input
            type="text"
            id="lapTimeString"
            value={formatLapTime(initialData.lapTime)}
            readOnly
            tabIndex={-1}
            className={readOnlyInputClass}
          />
        </div>
      </div>

      {/* Sector Times */}
      <div>
        <h3 className="text-sm font-medium text-secondary-700 mb-3">
          {t("pages:sessions.detail.laps.form.sectorsTitle")}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label
              htmlFor="sector1"
              className="block text-sm text-secondary-600 mb-1"
            >
              {t("pages:sessions.detail.laps.form.sector1")}
            </label>
            <input
              type="text"
              id="sector1"
              value={
                initialData.sectorTimes?.[0]
                  ? formatLapTime(initialData.sectorTimes[0])
                  : "—"
              }
              readOnly
              tabIndex={-1}
              className={readOnlyInputClass}
            />
          </div>
          <div>
            <label
              htmlFor="sector2"
              className="block text-sm text-secondary-600 mb-1"
            >
              {t("pages:sessions.detail.laps.form.sector2")}
            </label>
            <input
              type="text"
              id="sector2"
              value={
                initialData.sectorTimes?.[1]
                  ? formatLapTime(initialData.sectorTimes[1])
                  : "—"
              }
              readOnly
              tabIndex={-1}
              className={readOnlyInputClass}
            />
          </div>
          <div>
            <label
              htmlFor="sector3"
              className="block text-sm text-secondary-600 mb-1"
            >
              {t("pages:sessions.detail.laps.form.sector3")}
            </label>
            <input
              type="text"
              id="sector3"
              value={
                initialData.sectorTimes?.[2]
                  ? formatLapTime(initialData.sectorTimes[2])
                  : "—"
              }
              readOnly
              tabIndex={-1}
              className={readOnlyInputClass}
            />
          </div>
        </div>
      </div>

      {/* Speed */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="maxSpeed"
            className="block text-sm font-medium text-secondary-700 mb-2"
          >
            {t("pages:sessions.detail.laps.form.maxSpeed")}
          </label>
          <input
            type="text"
            id="maxSpeed"
            value={initialData.maxSpeed ?? "—"}
            readOnly
            tabIndex={-1}
            className={readOnlyInputClass}
          />
        </div>

        <div>
          <label
            htmlFor="avgSpeed"
            className="block text-sm font-medium text-secondary-700 mb-2"
          >
            {t("pages:sessions.detail.laps.form.avgSpeed")}
          </label>
          <input
            type="text"
            id="avgSpeed"
            value={initialData.avgSpeed ?? "—"}
            readOnly
            tabIndex={-1}
            className={readOnlyInputClass}
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <label
          htmlFor="notes"
          className="block text-sm font-medium text-secondary-700 mb-2"
        >
          {t("common:notes")}
        </label>
        <textarea
          id="notes"
          rows={3}
          {...register("notes")}
          className="input resize-none"
          placeholder={t("pages:sessions.detail.laps.form.notesPlaceholder")}
        />
        {errors.notes && (
          <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p>
        )}
      </div>

      {/* Valid Lap Toggle */}
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="isValid"
          {...register("isValid")}
          className="h-4 w-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
        />
        <label htmlFor="isValid" className="text-sm font-medium text-secondary-700">
          {t("pages:sessions.detail.laps.form.validLapLabel")}
        </label>
      </div>

      <div className="flex justify-end space-x-3 pt-6 border-t border-secondary-200">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary px-4 py-2"
          disabled={isLoading}
        >
          {t("common:cancel")}
        </button>
        <button
          type="submit"
          className="btn-primary px-4 py-2"
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t("common:updating")}
            </div>
          ) : (
            t("pages:sessions.detail.laps.form.update")
          )}
        </button>
      </div>
    </form>
  );
}
